/**
 * THE MATCH STAGE IS A PORTRAIT COLUMN, ON EVERY WINDOW.
 *
 * The board is 18x32 tiles — 9:16 by construction (ADR-0005, STYLE_BIBLE §5) —
 * and the HUD is a column over it, not a bar beside it. A landscape window
 * therefore cannot be filled by the game: aspect-fitting a 9:16 board into
 * 1440x900 gives a 344 px board, 24 % of the width, with ~550 px of nothing on
 * each side. Measured in Chrome that reads exactly as the owner described it —
 * "full screen black shit ... map is black screen".
 *
 * The fix is not to stretch the board. It is to stop pretending the drawing
 * surface is the window: the renderer gets a portrait BOX, centred, at most
 * `MATCH_STAGE_MAX_ASPECT` as wide as it is tall, and the room left over is a
 * framed backdrop rather than an unexplained void. Inside that box the desktop
 * layout is proportionally the phone layout — same board share, same HUD, one
 * set of numbers to reason about — and every pixel the renderer paints is a
 * pixel someone can see.
 *
 * No pixi, no DOM: this is arithmetic, so `bootstrap.ts` (which must not pull
 * the match chunk into the static graph) and the layout tests can both have it.
 */

/**
 * Widest the stage is ever allowed to be, as a fraction of its own height.
 *
 * 0.46 is the reference phone: 390/844 = 0.462. At that ratio the board — which
 * the HUD reserve makes height-bound at every size — fills ~92 % of the stage's
 * width, exactly as it does on the phone the game was designed against. A wider
 * cap would only add side margin the board can never reach, because the board's
 * height, not the window's width, is what bounds it.
 *
 * THE CAP ONLY BITES IN LANDSCAPE. A 320x568 phone is 0.563 and must keep its
 * full width: capping it would frame a small screen for no gain. `w > h` is the
 * whole test, and it is `@media (orientation: landscape)` in `styles/app.css`,
 * which is where the DOM side of this rule lives.
 */
export const MATCH_STAGE_MAX_ASPECT = 0.46;

export interface StageBox {
  readonly w: number;
  readonly h: number;
}

/** The stage box for a viewport, CSS px. Portrait windows are unchanged. */
export function matchStageBox(viewW: number, viewH: number): StageBox {
  const w = Number.isFinite(viewW) && viewW > 0 ? viewW : 1;
  const h = Number.isFinite(viewH) && viewH > 0 ? viewH : 1;
  if (w <= h) return { w, h };
  const cap = h * MATCH_STAGE_MAX_ASPECT;
  return { w: w < cap ? w : cap, h };
}

/**
 * The class the stylesheet hangs the rule above on. `styles/app.css` is the one
 * place the width is written as CSS, because the cap is conditional on
 * orientation and an inline style cannot carry a media query.
 */
export const MATCH_STAGE_CLASS = 'rga-stage';
